import React, { useEffect, useRef } from 'react';

const MonacoEditor = ({
                          value,
                          onChange,
                          language = 'python',
                          theme = 'vs-light',
                          height = '400px',
                          readOnly = false,
                          options = {},
                          onEditorReady = null
                      }) => {
    const editorRef = useRef(null);
    const monacoRef = useRef(null);
    const containerRef = useRef(null);
    const isEditorReady = useRef(false);

    useEffect(() => {
        // Monaco Editor 로드
        const loadMonaco = async () => {
            // Monaco Editor가 이미 로드되어 있는지 확인
            if (window.monaco) {
                initializeEditor();
                return;
            }

            // Monaco Editor 스크립트 동적 로드
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/monaco-editor/0.44.0/min/vs/loader.min.js';
            script.async = true;
            script.onload = () => {
                try {
                    // window.require가 정의될 때까지 대기
                    const waitForRequire = () => {
                        if (window.require && window.require.config) {
                            window.require.config({
                                paths: {
                                    'vs': 'https://cdnjs.cloudflare.com/ajax/libs/monaco-editor/0.44.0/min/vs'
                                }
                            });

                            window.require(['vs/editor/editor.main'], () => {
                                initializeEditor();
                            });
                        } else {
                            // require가 아직 없으면 100ms 후 재시도
                            setTimeout(waitForRequire, 100);
                        }
                    };
                    waitForRequire();
                } catch (error) {
                    console.error('Monaco loader error:', error);
                }
            };
            script.onerror = (error) => {
                console.error('Failed to load Monaco script:', error);
            };
            document.body.appendChild(script);
        };

        const initializeEditor = () => {
            if (!containerRef.current) return;

            // 기본 옵션 설정
            const defaultOptions = {
                value: value || '',
                language: language,
                theme: theme,
                automaticLayout: true,
                minimap: { enabled: false },
                fontSize: 14,
                lineNumbers: 'on',
                roundedSelection: false,
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                readOnly: readOnly,
                formatOnPaste: true,
                formatOnType: true,
                suggestOnTriggerCharacters: true,
                quickSuggestions: {
                    other: true,
                    comments: false,
                    strings: false
                },
                parameterHints: {
                    enabled: true
                },
                ...options
            };

            // 에디터 생성
            const editor = window.monaco.editor.create(containerRef.current, defaultOptions);
            editorRef.current = editor;
            monacoRef.current = window.monaco;
            isEditorReady.current = true;

            // 변경 이벤트 리스너
            editor.onDidChangeModelContent(() => {
                isUserTyping.current = true;
                const currentValue = editor.getValue();
                if (onChange) {
                    onChange(currentValue);
                }
                // 사용자 입력 플래그를 잠시 후 리셋
                setTimeout(() => {
                    isUserTyping.current = false;
                }, 100);
            });

            // Python 자동완성 향상을 위한 설정
            if (language === 'python') {
                configurePythonSupport();
            }

            // Notify parent that editor is ready
            if (onEditorReady) {
                onEditorReady(editor);
            }
        };

        const configurePythonSupport = () => {
            if (!monacoRef.current) return;

            // Python 키워드 및 내장 함수
            monacoRef.current.languages.registerCompletionItemProvider('python', {
                provideCompletionItems: (model, position) => {
                    const word = model.getWordUntilPosition(position);
                    const range = {
                        startLineNumber: position.lineNumber,
                        endLineNumber: position.lineNumber,
                        startColumn: word.startColumn,
                        endColumn: word.endColumn
                    };

                    const suggestions = [
                        // Python 키워드
                        ...['def', 'class', 'if', 'else', 'elif', 'for', 'while', 'try', 'except', 'finally',
                            'import', 'from', 'as', 'return', 'pass', 'break', 'continue', 'with', 'async',
                            'await', 'yield', 'lambda', 'global', 'nonlocal', 'assert', 'del', 'raise'].map(keyword => ({
                            label: keyword,
                            kind: monacoRef.current.languages.CompletionItemKind.Keyword,
                            insertText: keyword,
                            range: range
                        })),
                        // 내장 함수
                        ...['print', 'len', 'range', 'type', 'str', 'int', 'float', 'list', 'dict',
                            'set', 'tuple', 'input', 'open', 'round', 'abs', 'max', 'min', 'sum'].map(func => ({
                            label: func,
                            kind: monacoRef.current.languages.CompletionItemKind.Function,
                            insertText: func + '()',
                            range: range
                        }))
                    ];

                    return { suggestions };
                }
            });
        };

        loadMonaco();

        // Cleanup
        return () => {
            if (editorRef.current) {
                editorRef.current.dispose();
            }
        };
    }, []);

    // value prop 변경 시 에디터 내용 업데이트 (사용자 입력 시 제외)
    const isUserTyping = useRef(false);
    
    useEffect(() => {
        const updateEditorValue = () => {
            if (editorRef.current && isEditorReady.current && !isUserTyping.current) {
                const currentValue = editorRef.current.getValue();
                console.log('MonacoEditor value update:', { newValue: value, currentValue, shouldUpdate: value !== currentValue });
                
                if (value !== currentValue) {
                    try {
                        // 커서 위치 저장
                        const position = editorRef.current.getPosition();
                        editorRef.current.setValue(value || '');
                        // 커서 위치 복원
                        if (position) {
                            editorRef.current.setPosition(position);
                        }
                        console.log('MonacoEditor setValue called with:', value);
                    } catch (error) {
                        console.error('Error setting Monaco editor value:', error);
                        // If editor isn't ready, try again after a short delay
                        setTimeout(updateEditorValue, 100);
                    }
                }
            } else if (!isEditorReady.current && !isUserTyping.current) {
                // If editor isn't ready yet, try again after a short delay
                console.log('Editor not ready, retrying in 100ms');
                setTimeout(updateEditorValue, 100);
            }
        };

        // 사용자 입력이 아닌 외부에서 value가 변경될 때만 업데이트
        if (value !== undefined && !isUserTyping.current) {
            updateEditorValue();
        }
    }, [value]);

    // language 변경 시 모델 언어 업데이트
    useEffect(() => {
        if (editorRef.current && monacoRef.current) {
            const model = editorRef.current.getModel();
            if (model) {
                monacoRef.current.editor.setModelLanguage(model, language);
            }
        }
    }, [language]);

    // theme 변경 시 테마 업데이트
    useEffect(() => {
        if (monacoRef.current) {
            monacoRef.current.editor.setTheme(theme);
        }
    }, [theme]);

    return (
        <div
            ref={containerRef}
            style={{
                height: height,
                width: '100%',
                border: '1px solid var(--card-border)',
                borderRadius: '8px',
                overflow: 'hidden'
            }}
        />
    );
};

export default MonacoEditor;